import { SignIn, SignUp, useAuth } from "@clerk/clerk-react";
import { Box, Button, Typography } from "@mui/material";
import powerByLogo from "assets/logos/logo-transparent.png";
import { Navigate, useLocation } from "react-router-dom";
import { routes } from "schemas/routes";

import Loading from "components/Loading";
import { LANDING_PAGE_LINK } from "constants/influencer";
import { getNativeBrowserLink, isWebView } from "utils/device";
import { getSource } from "utils/tracking";

import InfluencerLayout from "./Layout/Influencer";
import { AuthAction } from "./schema";
import styles from "./styles";

interface AuthPageProps {
  action: AuthAction;
  prevLink: string;
}

const AuthPage = ({ action, prevLink }: AuthPageProps) => {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  const isSocialFlow = getSource() === "instagram" || getSource() === "tiktok";

  const getSignInRedirectUrl = () => {
    if (isSocialFlow) {
      return `/${routes.preferencesOnboarding}${location.search}`;
    } else if (
      prevLink?.includes(routes.dashboard) ||
      prevLink?.includes(routes.onboardingInfluencer)
    ) {
      return prevLink;
    }
    return `/${routes.home}${location.search}`;
  };

  const signInRedirectUrl = getSignInRedirectUrl();
  const signUpRedirectUrl = `/${routes.onboardingInfluencer}${location.search}`;
  const emailMatch = location.search?.match(/email=([^&]*)/);
  const email = emailMatch ? decodeURIComponent(emailMatch[1]) : "";

  if (window.location.hostname === "crm.hatchways.io") {
    const newPath = "https://app.onbento.com" + window.location.pathname;
    window.location.href = newPath;
    return null;
  }

  if (isSignedIn) {
    return <Navigate to={signInRedirectUrl} replace />;
  }

  const appearance = {
    layout: {
      logoLinkUrl: LANDING_PAGE_LINK,
      privacyPageUrl: "https://www.onbento.com/privacy-policy",
    },
    elements: {
      card: {
        width: { xs: "auto", md: "100%" },
        boxShadow: "none",
      },
    },
  };
  if (isLoaded) {
    if (isWebView()) {
      return (
        <InfluencerLayout>
          <Box sx={styles.form}>
            <Box component="img" src={powerByLogo} sx={styles.poweredByLogo} />
            <Typography variant="h5" sx={styles.title}>
              Join Bento
            </Typography>
            <Typography sx={styles.subtitle}>
              Click the button below to login or create a new account
            </Typography>
            <a
              href={getNativeBrowserLink(signInRedirectUrl)}
              target="_blank"
              rel="noreferrer"
            >
              <Button sx={styles.button} variant="contained" disableElevation>
                Access Bento{" "}
                <Box
                  component="i"
                  className="fa-regular fa-arrow-right"
                  sx={{ ml: 1 }}
                />
              </Button>
            </a>
          </Box>
        </InfluencerLayout>
      );
    } else {
      return (
        <InfluencerLayout>
          {action === AuthAction.SIGN_UP && (
            <SignUp
              initialValues={{
                emailAddress: email || "",
              }}
              signInUrl={`/${routes.signIn}`}
              afterSignUpUrl={signUpRedirectUrl}
              afterSignInUrl={signInRedirectUrl}
              appearance={appearance}
            />
          )}
          {action === AuthAction.SIGN_IN && (
            <SignIn
              initialValues={{
                emailAddress: email || "",
              }}
              signUpUrl={`/${routes.signUp}`}
              afterSignUpUrl={signUpRedirectUrl}
              afterSignInUrl={signInRedirectUrl}
              appearance={appearance}
            />
          )}
        </InfluencerLayout>
      );
    }
  } else {
    return <Loading />;
  }
};

export default AuthPage;
